import type { CompanyTableColumnDef, CompanyTableColumnType } from '../types/company-table-column.types';

const currencyFormatter = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });
const percentFormatter = new Intl.NumberFormat('pt-BR', {
  style: 'percent',
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
});

function formatPtBrNumber(value: number, decimals: number): string {
  return new Intl.NumberFormat('pt-BR', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value);
}

function parseDateValue(value: unknown): Date | null {
  if (value instanceof Date && !Number.isNaN(value.getTime())) return value;
  if (typeof value === 'number' && Number.isFinite(value)) {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  if (typeof value === 'string' && value.trim()) {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  return null;
}

export interface FormatTableCellOptions {
  numberFractionDigits?: number;
}

export function formatTableCell(
  value: unknown,
  column: Pick<CompanyTableColumnDef, 'field' | 'type'>,
  options: FormatTableCellOptions = {},
): string {
  const type: CompanyTableColumnType = column.type ?? 'text';
  const decimals = options.numberFractionDigits ?? 2;

  if (value === null || value === undefined || value === '') {
    return '—';
  }

  switch (type) {
    case 'boolean': {
      if (typeof value === 'boolean') return value ? 'Sim' : 'Não';
      const s = String(value).toLowerCase();
      if (s === 'true' || s === '1' || s === 'yes') return 'Sim';
      if (s === 'false' || s === '0' || s === 'no') return 'Não';
      return String(value);
    }
    case 'number': {
      const n = typeof value === 'number' ? value : Number(value);
      return Number.isFinite(n) ? formatPtBrNumber(n, decimals) : String(value);
    }
    case 'currency': {
      const n = typeof value === 'number' ? value : Number(value);
      return Number.isFinite(n) ? currencyFormatter.format(n) : String(value);
    }
    case 'percent': {
      const n = typeof value === 'number' ? value : Number(value);
      if (!Number.isFinite(n)) return String(value);
      const ratio = n > 1 ? n / 100 : n;
      return percentFormatter.format(ratio);
    }
    case 'date': {
      const d = parseDateValue(value);
      return d ? d.toLocaleDateString('pt-BR') : String(value);
    }
    case 'dateTime': {
      const d = parseDateValue(value);
      return d ? d.toLocaleString('pt-BR') : String(value);
    }
    case 'status':
    case 'text':
    default:
      return String(value);
  }
}
